/**
 * Locality tier — classifies a French commune by population so that
 * downstream env blocks (écoles, commerces, transports) can adapt their
 * search radius.
 *
 * Source : `geo.api.gouv.fr/communes/{insee}?fields=population` (IGN /
 * Etalab, CORS ✅, anonyme, pas de quota). INSEE refreshes commune
 * populations annually ; we cache in-memory per tab session and accept
 * re-fetching on reload — info churn is glacial.
 *
 * Tier thresholds follow the rough INSEE "gamme d'unité urbaine" — not
 * the grille de densité, because densité requires the commune's surface
 * (an extra API field that geo.api.gouv.fr returns in hectares-or-km²
 * depending on endpoint, adding ambiguity without much benefit for our
 * use case).
 *
 * Edge case : Paris / Lyon / Marseille arrondissements are separate INSEE
 * codes (75101–75120, 13201–13216, 69381–69389) with populations
 * 15k–200k. Left unchecked, Paris 4e (27k hab.) would route to "ville
 * moyenne" — clearly wrong from a user's perspective. We short-circuit
 * these ranges straight to "metropole".
 */

import { useEffect, useState } from "react";

export type LocalityTier =
  | "rural" // < 2 000 hab
  | "petite" // 2 000 – 10 000
  | "moyenne" // 10 000 – 50 000
  | "grande" // 50 000 – 200 000
  | "metropole"; // ≥ 200 000 (+ arrondissements Paris/Lyon/Marseille)

export const TIER_LABEL: Record<LocalityTier, string> = {
  rural: "rural",
  petite: "petite ville",
  moyenne: "ville moyenne",
  grande: "grande ville",
  metropole: "métropole",
};

export type CommuneInfo = {
  insee: string;
  population: number;
  tier: LocalityTier;
};

// ─── Classification ─────────────────────────────────────────────────────

/** Upper-bound population for each tier, in hab. `metropole` has no cap. */
const TIER_CAPS: { tier: LocalityTier; cap: number }[] = [
  { tier: "rural", cap: 2_000 },
  { tier: "petite", cap: 10_000 },
  { tier: "moyenne", cap: 50_000 },
  { tier: "grande", cap: 200_000 },
];

function isArrondissement(insee: string): boolean {
  // Comparing the 5-char codes as strings works because all three ranges
  // are contiguous and left-anchored on the same prefix.
  if (insee >= "75101" && insee <= "75120") return true; // Paris
  if (insee >= "13201" && insee <= "13216") return true; // Marseille
  if (insee >= "69381" && insee <= "69389") return true; // Lyon
  return false;
}

export function tierFor(insee: string, population: number): LocalityTier {
  if (isArrondissement(insee)) return "metropole";
  for (const { tier, cap } of TIER_CAPS) {
    if (population < cap) return tier;
  }
  return "metropole";
}

// ─── Fetch + cache ──────────────────────────────────────────────────────

const memCache = new Map<string, CommuneInfo>();
const inflight = new Map<string, Promise<CommuneInfo>>();

// Fallback used when geo.api.gouv.fr is unreachable — a "petite ville"
// sits close to the French median commune (INSEE reports ~55 % of
// communes under 500 hab but most *users* geocode in communes > 2 k).
// Worst case the user gets slightly-off radii; never a hard failure.
const FALLBACK_TIER: LocalityTier = "petite";

export async function fetchCommuneInfo(insee: string): Promise<CommuneInfo> {
  const hit = memCache.get(insee);
  if (hit) return hit;
  const pending = inflight.get(insee);
  if (pending) return pending;

  const p = (async () => {
    try {
      const res = await fetch(
        `https://geo.api.gouv.fr/communes/${encodeURIComponent(insee)}?fields=population`,
        { signal: AbortSignal.timeout(8_000) },
      );
      if (!res.ok) throw new Error(`geo.api.gouv.fr HTTP ${res.status}`);
      const body = (await res.json()) as { population?: number };
      const pop = typeof body.population === "number" ? body.population : 0;
      const info: CommuneInfo = {
        insee,
        population: pop,
        tier: tierFor(insee, pop),
      };
      memCache.set(insee, info);
      return info;
    } catch (e) {
      console.warn(`[localityTier] fallback for ${insee}`, e);
      const info: CommuneInfo = {
        insee,
        population: 0,
        tier: FALLBACK_TIER,
      };
      // Don't memoize the fallback — next caller should re-try the API.
      return info;
    }
  })();
  inflight.set(insee, p);
  try {
    return await p;
  } finally {
    inflight.delete(insee);
  }
}

// ─── Hook ───────────────────────────────────────────────────────────────

/**
 * `useCommuneInfo(insee)` — returns `null` while the lookup is in flight,
 * then the commune info. The hook is idempotent : repeated calls for the
 * same insee during a session all resolve from the in-memory cache.
 */
export function useCommuneInfo(insee: string | null): CommuneInfo | null {
  const [info, setInfo] = useState<CommuneInfo | null>(() =>
    insee ? (memCache.get(insee) ?? null) : null,
  );
  useEffect(() => {
    if (!insee) {
      setInfo(null);
      return;
    }
    const hit = memCache.get(insee);
    if (hit) {
      setInfo(hit);
      return;
    }
    let cancelled = false;
    fetchCommuneInfo(insee).then((i) => {
      if (!cancelled) setInfo(i);
    });
    return () => {
      cancelled = true;
    };
  }, [insee]);
  return info;
}
